import React from 'react';
import Creatable from 'react-select/creatable';
import { classnames } from '@ygtechnologies/bygr-frontend-component-library';
import { t } from '@lingui/core/macro';
import theme from './MappingValueSelect.module.scss';

interface Option {
    value: string;
    label: string;
}

export interface MappingValueGroup {
    label: string;
    values: string[];
}

interface MappingValueSelectProps {
    value: string;
    onChange(value: string): void;
    /** Flat list of suggested values. Ignored when `groups` is provided. */
    options?: string[];
    /** Suggested values grouped under headings (e.g. by 3D configurator). */
    groups?: MappingValueGroup[];
    placeholder?: string;
    dirty?: boolean;
    invalid?: boolean;
    className?: string;
}

// Dropdown of known configurator values that still accepts custom typed values (creatable). Used in
// the "configurator first" workflow; the page falls back to a plain text input when none exist.
export const MappingValueSelect: React.FC<MappingValueSelectProps> = ({
    value,
    onChange,
    options,
    groups,
    placeholder,
    dirty,
    invalid,
    className,
}) => {
    const toOption = (o: string): Option => ({ value: o, label: o });
    const selectOptions: any[] = groups
        ? groups.map((g) => ({ label: g.label, options: g.values.map(toOption) }))
        : (options ?? []).map(toOption);

    return (
        <Creatable
            classNamePrefix="r-s"
            className={classnames(
                'react-select',
                theme.select,
                { [theme.dirty]: dirty, [theme.invalid]: invalid },
                className
            )}
            placeholder={placeholder}
            value={value ? { value, label: value } : null}
            options={selectOptions}
            isClearable
            onChange={(opt: any) => onChange((opt as Option | null)?.value ?? '')}
            noOptionsMessage={() => t`No configurator values`}
            // Render the menu in a body-level portal so it isn't clipped by the table's overflow.
            menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
            menuPosition="fixed"
            styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
        />
    );
};
